import { parseGPX, MetaData } from "@we-gold/gpxjs";
import {
  Coordinate,
  reduceCoordinatesFavouringDistance,
} from "./use-coordinates";
import { extractGPSData } from "PosterEditor/utils/gps-data";
import { GPSData } from "PosterEditor/model/poster-project";

interface UseGPXParserProps {}

export const useGPXParser = (props: UseGPXParserProps) => {
  const parse = (gpx: string) => {
    const [parsedFile, error] = parseGPX(gpx);

    if (error) throw error;

    const geoJSON = parsedFile.toGeoJSON();
    return geoJSON;
  };

  return {
    parse,
  };
};

const parseGPXToGeoJSON = (gpx: string) => {
  const [parsedFile, error] = parseGPX(gpx);

  if (error) throw error;
  var geoJSON = parsedFile.toGeoJSON();

  const properties: MetaData = {
    name: parsedFile.tracks[0].name,
    description: parsedFile.tracks[0].description,
    time: parsedFile.metadata.time,
    author: parsedFile.metadata.author,
    link: parsedFile.metadata.link,
  };
  geoJSON.properties = properties;

  return geoJSON;
};

export const getCoordinatesFromGPXStringX = (
  gpxString: string,
  desiredLength?: number
): {
  name: string | undefined;
  coordinates: Coordinate[];
  data: GPSData | undefined;
} => {
  const geoJSON = parseGPXToGeoJSON(gpxString);
  const name = geoJSON.properties.name ?? undefined;

  const coordinates = geoJSON.features[0].geometry.coordinates;
  // Each coordinate is an array of [lng, lat, eleveation]
  const array: Coordinate[] = coordinates
    .map((coord) => {
      const array = coord as number[];
      if (array.length > 1) {
        return { lat: array[1], lng: array[0] };
      }
      return null;
    })
    .filter((coord): coord is Coordinate => coord !== null);
  const data = extractGPSData({ allCoordinates: array });
  if (desiredLength) {
    const paredDown = reduceCoordinatesFavouringDistance(array, desiredLength);
    return { name, coordinates: paredDown, data };
  }
  return { name, coordinates: array, data };
};

export const getCoordinatesFromGPXString = (
  gpxString: string,
  desiredLength?: number
): {
  name: string | undefined;
  coordinates: Coordinate[];
  data: GPSData | undefined;
} => {
  const [parsedFile, error] = parseGPX(gpxString);
  if (error) throw error;

  // Try tracks first, then routes if no tracks available
  let name: string | undefined;
  let allPoints: any[] = [];

  if (parsedFile.tracks && parsedFile.tracks.length > 0) {
    name = parsedFile.tracks[0]?.name ?? undefined;
    // Flatten all segments of all tracks
    allPoints = parsedFile.tracks.flatMap((track) => track.points);
  } else if (parsedFile.routes && parsedFile.routes.length > 0) {
    name = parsedFile.routes[0]?.name ?? undefined;
    // Flatten all route points from all routes
    allPoints = parsedFile.routes.flatMap((route) => route.points);
  } else {
    throw new Error("GPX file contains no tracks or routes");
  }

  const fullCoordinates: Coordinate[] = allPoints.map((pt) => ({
    lat: pt.latitude,
    lng: pt.longitude,
    elevation: pt.elevation ?? undefined,
    time: pt.time?.getTime(), // or just `pt.time` if you want a Date
  }));
  const data = extractGPSData({ allCoordinates: fullCoordinates });
  let coordinates: Coordinate[] = fullCoordinates;

  if (desiredLength) {
    coordinates = reduceCoordinatesFavouringDistance(
      coordinates,
      desiredLength
    );
  }
  return { name, coordinates, data };
};
